/**
 * src/lib/knowledge/references.ts
 * Reliable reading library across three tiers:
 * - introductory (accessible, foundational overviews)
 * - intermediate (core standard texts and scholarly treatises)
 * - advanced (seminal formal treatises, dense monographs, and foundational papers)
 *
 * Implements:
 * - Level classification: "introductory" | "intermediate" | "advanced"
 * - Source type flag: "primary" | "secondary"
 * - Linked topics and pages supported
 * - Accurate author, title, historical dates, and verified links
 *
 * Note on links: Links to Stanford Encyclopedia of Philosophy (SEP) and Internet
 * Encyclopedia of Philosophy (IEP) adhere strictly to stable, verified canonical addresses.
 */

export type ReferenceLevel = "introductory" | "intermediate" | "advanced";
export type ReferenceType = "primary" | "secondary";

export interface ReferenceEntry {
  id: string;
  author: string;
  title: string;
  year: string;
  edition?: string;
  level: ReferenceLevel;
  type: ReferenceType;
  pagesSupported: { title: string; route: string }[];
  description: string;
  url?: string;
}

export const REFERENCES: ReferenceEntry[] = [
  // --- INTRODUCTORY TIER ---
  {
    id: "warburton-thinking",
    author: "Nigel Warburton",
    title: "Thinking from A to Z",
    year: "1996",
    edition: "3rd ed. 2007",
    level: "introductory",
    type: "secondary",
    pagesSupported: [
      { title: "Critical Thinking Workshop", route: "/CriticalThinkingPage" },
      { title: "Fallacy Library", route: "/FallacyLibraryPage" },
    ],
    description: "An accessible pocket guide clarifying core philosophical tools, argumentative pitfalls, informal fallacies, and rhetorical maneuvers with direct, everyday examples.",
  },
  {
    id: "russell-problems",
    author: "Bertrand Russell",
    title: "The Problems of Philosophy",
    year: "1912",
    level: "introductory",
    type: "primary",
    pagesSupported: [
      { title: "Philosophy as Investigation", route: "/PhilosophyAsInvestigationPage" },
      { title: "Knowledge Laboratory", route: "/KnowledgeLaboratoryPage" },
    ],
    description: "Russell's classic, lucid introduction to the nature of reality, appearance versus reality, the limits of philosophical knowledge, and induction.",
    url: "https://plato.stanford.edu/entries/russell/",
  },
  {
    id: "kahneman-thinking",
    author: "Daniel Kahneman",
    title: "Thinking, Fast and Slow",
    year: "2011",
    level: "introductory",
    type: "secondary",
    pagesSupported: [
      { title: "Critical Thinking Workshop", route: "/CriticalThinkingPage" },
      { title: "Fallacy Library", route: "/FallacyLibraryPage" },
    ],
    description: "A comprehensive synthesis of behavioral economics and cognitive psychology exploring dual-process cognition (System 1 heuristics and System 2 deliberation).",
  },
  {
    id: "sep-epistemology",
    author: "Matthias Steup & Ram Neta",
    title: "Epistemology",
    year: "2005",
    edition: "Substantive revision 2020",
    level: "introductory",
    type: "secondary",
    pagesSupported: [
      { title: "Knowledge Laboratory", route: "/KnowledgeLaboratoryPage" },
    ],
    description: "The authoritative Stanford Encyclopedia of Philosophy overview detailing theories of justification, internalism vs externalism, and the nature of knowledge.",
    url: "https://plato.stanford.edu/entries/epistemology/",
  },
  {
    id: "sep-fallacies",
    author: "Hans Hansen",
    title: "Fallacies",
    year: "2020",
    level: "introductory",
    type: "secondary",
    pagesSupported: [
      { title: "Fallacy Library", route: "/FallacyLibraryPage" },
    ],
    description: "Comprehensive Stanford Encyclopedia of Philosophy entry charting the historical evolution of fallacy theory from Aristotle to contemporary dialectics.",
    url: "https://plato.stanford.edu/entries/fallacies/",
  },

  // --- INTERMEDIATE TIER ---
  {
    id: "plato-meno",
    author: "Plato",
    title: "Meno",
    year: "c. 385 BCE",
    level: "intermediate",
    type: "primary",
    pagesSupported: [
      { title: "Philosophy as Investigation", route: "/PhilosophyAsInvestigationPage" },
      { title: "Knowledge Laboratory", route: "/KnowledgeLaboratoryPage" },
    ],
    description: "Foundational Socratic dialogue exploring the definition of virtue, learning as recollection, and the famous distinction between true belief and justified knowledge.",
    url: "https://plato.stanford.edu/entries/plato-ethics/",
  },
  {
    id: "descartes-meditations",
    author: "René Descartes",
    title: "Meditations on First Philosophy (Meditationes de Prima Philosophia)",
    year: "1641",
    level: "intermediate",
    type: "primary",
    pagesSupported: [
      { title: "Philosophy as Investigation", route: "/PhilosophyAsInvestigationPage" },
      { title: "Knowledge Laboratory", route: "/KnowledgeLaboratoryPage" },
      { title: "Simulation Theory", route: "/SimulationTheoryPage" },
    ],
    description: "The foundational text of modern rationalism and radical skepticism, introducing the dream argument, the evil demon hypothesis, and the indubitable 'Cogito'.",
    url: "https://plato.stanford.edu/entries/descartes-epistemology/",
  },
  {
    id: "hume-enquiry",
    author: "David Hume",
    title: "An Enquiry Concerning Human Understanding",
    year: "1748",
    level: "intermediate",
    type: "primary",
    pagesSupported: [
      { title: "Knowledge Laboratory", route: "/KnowledgeLaboratoryPage" },
      { title: "Critical Thinking Workshop", route: "/CriticalThinkingPage" },
    ],
    description: "Seminal British empiricist critique establishing Hume's Fork (relations of ideas vs matters of fact), the problem of induction, and the nature of causal inference.",
    url: "https://plato.stanford.edu/entries/hume/",
  },
  {
    id: "mill-utilitarianism",
    author: "John Stuart Mill",
    title: "Utilitarianism",
    year: "1863",
    level: "intermediate",
    type: "primary",
    pagesSupported: [
      { title: "Critical Thinking Workshop", route: "/CriticalThinkingPage" },
      { title: "Philosophy as Investigation", route: "/PhilosophyAsInvestigationPage" },
    ],
    description: "Classic philosophical treatise on consequentialist normative ethics, distinguishing quantitative and qualitative pleasures and exploring moral justification.",
    url: "https://plato.stanford.edu/entries/mill/",
  },
  {
    id: "tversky-kahneman-heuristics",
    author: "Amos Tversky & Daniel Kahneman",
    title: "Judgment under Uncertainty: Heuristics and Biases",
    year: "1974",
    edition: "Science, 185(4157), pp. 1124–1131",
    level: "intermediate",
    type: "primary",
    pagesSupported: [
      { title: "Critical Thinking Workshop", route: "/CriticalThinkingPage" },
      { title: "Fallacy Library", route: "/FallacyLibraryPage" },
    ],
    description: "Groundbreaking empirical paper identifying representativeness, availability, and anchoring heuristics, demonstrating systematic human errors in statistical inference.",
  },
  {
    id: "gettier-analysis",
    author: "Edmund L. Gettier",
    title: "Is Justified True Belief Knowledge?",
    year: "1963",
    edition: "Analysis, 23(6), pp. 121–123",
    level: "intermediate",
    type: "primary",
    pagesSupported: [
      { title: "Knowledge Laboratory", route: "/KnowledgeLaboratoryPage" },
      { title: "Philosophy as Investigation", route: "/PhilosophyAsInvestigationPage" },
    ],
    description: "A three-page paper that dismantled the classic tripartite definition of knowledge by presenting two decisive counterexamples involving justified true belief formed by epistemic luck.",
    url: "https://plato.stanford.edu/entries/knowledge-analysis/",
  },
  {
    id: "bostrom-simulation",
    author: "Nick Bostrom",
    title: "Are You Living in a Computer Simulation?",
    year: "2003",
    edition: "Philosophical Quarterly, 53(211), pp. 243–255",
    level: "intermediate",
    type: "primary",
    pagesSupported: [
      { title: "Simulation Theory", route: "/SimulationTheoryPage" },
      { title: "Philosophy as Investigation", route: "/PhilosophyAsInvestigationPage" },
    ],
    description: "The seminal trilemma argument proving that if advanced civilizations develop immense computing power to simulate ancestors, we are almost certainly simulated beings.",
  },
  {
    id: "sep-skepticism",
    author: "Peter Klein",
    title: "Skepticism",
    year: "2001",
    edition: "Substantive revision 2015",
    level: "intermediate",
    type: "secondary",
    pagesSupported: [
      { title: "Knowledge Laboratory", route: "/KnowledgeLaboratoryPage" },
      { title: "Philosophy as Investigation", route: "/PhilosophyAsInvestigationPage" },
    ],
    description: "Detailed scholarly analysis of philosophical skepticism, Agrippa's trilemma, Cartesian doubt, and contemporary epistemic responses.",
    url: "https://plato.stanford.edu/entries/skepticism/",
  },
  {
    id: "sep-logic-classical",
    author: "Stewart Shapiro & Teresa Kouri Kissel",
    title: "Classical Logic",
    year: "2000",
    edition: "Substantive revision 2021",
    level: "intermediate",
    type: "secondary",
    pagesSupported: [
      { title: "Logic Laboratory", route: "/LogicLaboratoryPage" },
    ],
    description: "Scholarly overview of propositional calculus, first-order predicate logic, semantic models, soundness, and completeness theorems.",
    url: "https://plato.stanford.edu/entries/logic-classical/",
  },

  // --- ADVANCED TIER ---
  {
    id: "aristotle-prior-analytics",
    author: "Aristotle",
    title: "Prior Analytics (Analytica Priora)",
    year: "c. 350 BCE",
    level: "advanced",
    type: "primary",
    pagesSupported: [
      { title: "Logic Laboratory", route: "/LogicLaboratoryPage" },
    ],
    description: "The founding text of formal deductive logic in the Western canon, formulating categorical syllogisms, mood and figure classifications, and direct proofs.",
    url: "https://plato.stanford.edu/entries/aristotle-logic/",
  },
  {
    id: "kant-critique",
    author: "Immanuel Kant",
    title: "Critique of Pure Reason (Kritik der reinen Vernunft)",
    year: "1781",
    edition: "2nd ed. (B-Kritik) 1787",
    level: "advanced",
    type: "primary",
    pagesSupported: [
      { title: "Philosophy as Investigation", route: "/PhilosophyAsInvestigationPage" },
      { title: "Knowledge Laboratory", route: "/KnowledgeLaboratoryPage" },
    ],
    description: "Monumental philosophical synthesis reconciling rationalism and empiricism through transcendental idealism, synthetic a priori judgments, and the categories of understanding.",
    url: "https://plato.stanford.edu/entries/kant-judgment/",
  },
  {
    id: "popper-logic-discovery",
    author: "Karl R. Popper",
    title: "The Logic of Scientific Discovery (Logik der Forschung)",
    year: "1934",
    edition: "English translation 1959",
    level: "advanced",
    type: "primary",
    pagesSupported: [
      { title: "Philosophy as Investigation", route: "/PhilosophyAsInvestigationPage" },
      { title: "Critical Thinking Workshop", route: "/CriticalThinkingPage" },
    ],
    description: "Popper's groundbreaking treatise introducing the demarcation criterion of falsifiability, rejecting verificationist inductivism in favor of deductive conjecture and refutation.",
    url: "https://plato.stanford.edu/entries/popper/",
  },
  {
    id: "sep-knowledge-analysis",
    author: "Jonathan Jenkins Ichikawa & Matthias Steup",
    title: "The Analysis of Knowledge",
    year: "2001",
    edition: "Substantive revision 2017",
    level: "advanced",
    type: "secondary",
    pagesSupported: [
      { title: "Knowledge Laboratory", route: "/KnowledgeLaboratoryPage" },
    ],
    description: "Exhaustive critical examination of post-Gettier attempts to define knowledge (no-false-lemmas, defeasibility, sensitivity, safety, and virtue conditions).",
    url: "https://plato.stanford.edu/entries/knowledge-analysis/",
  },
  {
    id: "iep-general",
    author: "Internet Encyclopedia of Philosophy Editors",
    title: "The Internet Encyclopedia of Philosophy",
    year: "1995",
    level: "advanced",
    type: "secondary",
    pagesSupported: [
      { title: "Philosophy as Investigation", route: "/PhilosophyAsInvestigationPage" },
      { title: "Logic Laboratory", route: "/LogicLaboratoryPage" },
      { title: "Knowledge Laboratory", route: "/KnowledgeLaboratoryPage" },
    ],
    description: "Peer-reviewed digital scholarly encyclopedia dedicated to academic philosophy, featuring comprehensive surveys of epistemology, logic, and philosophy of mind.",
    url: "https://iep.utm.edu/",
  },
];
